import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
};

const Avatar = ({ username, color, size = 64, imageUrl }) => (
  imageUrl ? (
    <img src={imageUrl} alt={username} style={{
      width: size, height: size, borderRadius: '50%', objectFit: 'cover',
      border: '2px solid #2a2a2a', flexShrink: 0, display: 'block',
    }} />
  ) : (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: color || '#c9a96e',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Cormorant Garamond, serif', fontSize: size * 0.36, fontWeight: 500, color: '#0a0a0a', flexShrink: 0,
      border: '2px solid #2a2a2a',
    }}>
      {username?.slice(0, 2).toUpperCase()}
    </div>
  )
);

const RatingDots = ({ value, max = 5 }) => (
  <div style={{ display: 'flex', gap: '4px' }}>
    {Array.from({ length: max }, (_, i) => (
      <span key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: i < value ? '#c9a96e' : '#2a2a2a', display: 'inline-block' }} />
    ))}
  </div>
);

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const longevityLabels = ['', '2hrs', '4hrs', '6hrs', 'All day', 'Next day'];
const sillageLabels = ['', 'Personal', 'Close range', 'Nearby', 'Room filler', 'Trail'];

const PublicProfile = ({ token, currentUser }) => {
  const isMobile = useIsMobile();
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('wears');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [expandedEntry, setExpandedEntry] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProfile(); }, [username]);

  const fetchProfile = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/api/profiles/${username}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
      setIsFollowing(res.data.stats.is_following);
      setFollowerCount(parseInt(res.data.stats.follower_count));
    } catch (err) {
      setError(err.response?.status === 404 ? 'User not found' : 'This profile is private');
    }
    setLoading(false);
  };

  const handleFollow = async () => {
    try {
      if (isFollowing) {
        await axios.delete(`${API}/api/follows/${data.user.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setIsFollowing(false); setFollowerCount(c => c - 1);
      } else {
        await axios.post(`${API}/api/follows/${data.user.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setIsFollowing(true); setFollowerCount(c => c + 1);
      }
    } catch (err) { console.error(err); }
  };

  const tabStyle = (active) => ({
    padding: isMobile ? '12px 20px' : '12px 28px',
    fontSize: '0.62rem', letterSpacing: '0.3em', textTransform: 'uppercase',
    color: active ? '#c9a96e' : '#3a3a3a', cursor: 'pointer', border: 'none', background: 'none',
    fontFamily: 'Jost, sans-serif', borderBottom: active ? '2px solid #c9a96e' : '2px solid transparent',
    marginBottom: '-1px', whiteSpace: 'nowrap',
  });

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingTop: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', color: '#c9a96e', fontStyle: 'italic' }}>Loading...</p>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingTop: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', padding: '0 24px' }}>
        <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 300, color: '#f5f0eb', fontStyle: 'italic', marginBottom: '12px' }}>{error}</p>
        <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: '#3a3a3a' }}>flakon.app/u/{username}</p>
      </div>
    </div>
  );

  const { user, library, recent_wears, stats } = data;
  const isOwnProfile = currentUser?.id === user.id;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingTop: isMobile ? '88px' : '100px', paddingBottom: '80px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 32px' }}>

        {/* Profile Header */}
        <div style={{ marginBottom: '40px', paddingBottom: '36px', borderBottom: '1px solid #1a1a1a' }}>

          {/* Banner */}
          <div style={{
            height: isMobile ? '90px' : '110px',
            background: 'linear-gradient(135deg, #111111 0%, #1a1510 50%, #111111 100%)',
            border: '1px solid #2a2a2a',
            marginLeft: isMobile ? '-16px' : '-32px',
            marginRight: isMobile ? '-16px' : '-32px',
            marginBottom: isMobile ? '-32px' : '-44px',
          }} />

          {/* Avatar row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px', position: 'relative' }}>
            <div style={{ border: '3px solid #0a0a0a', borderRadius: '50%', flexShrink: 0 }}>
              <Avatar username={user.username} color={user.avatar_color} size={isMobile ? 64 : 88} imageUrl={user.profile_picture_url} />
            </div>
            {!isOwnProfile && (
              <button onClick={handleFollow} style={{
                background: isFollowing ? 'none' : '#c9a96e',
                border: '1px solid #c9a96e',
                color: isFollowing ? '#c9a96e' : '#0a0a0a',
                fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em',
                textTransform: 'uppercase', padding: isMobile ? '10px 16px' : '12px 28px',
                cursor: 'pointer', transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                {isFollowing ? 'Following' : 'Follow'}
              </button>
            )}
          </div>

          {/* Username + bio */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.35em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '4px' }}>
              Flakon Member
            </p>
            <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.8rem' : '2.6rem', fontWeight: 300, color: '#f5f0eb', lineHeight: 1, marginBottom: '6px' }}>
              {user.username ? user.username.charAt(0).toUpperCase() + user.username.slice(1) : ''}
            </h1>
            {user.favourite_house && (
              <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.62rem', color: '#6b6460', letterSpacing: '0.05em', marginBottom: '4px' }}>
                <span style={{ color: '#3a3a3a' }}>Favourite house — </span>{user.favourite_house}
              </p>
            )}
            {user.created_at && (
              <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', color: '#3a3a3a', letterSpacing: '0.05em', marginBottom: user.profile_bio ? '12px' : '0' }}>
                Member since {new Date(user.created_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
              </p>
            )}
            {user.profile_bio && (
              <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.82rem', color: '#6b6460', fontWeight: 300, lineHeight: 1.7, maxWidth: '560px' }}>{user.profile_bio}</p>
            )}
          </div>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: '1px', background: '#1a1a1a', border: '1px solid #1a1a1a' }}>
            {[
              { value: stats.fragrance_count, label: 'Fragrances' },
              { value: followerCount, label: 'Followers' },
              { value: stats.following_count, label: 'Following' },
              ...(stats.top_accord ? [{ value: stats.top_accord.split(' ')[0], label: 'Top Accord' }] : []),
              ...(stats.most_worn ? [{ value: stats.most_worn.split(' ').slice(-1)[0], label: 'Most Worn' }] : []),
            ].map(({ value, label }, i) => (
              <div key={i} style={{ flex: 1, background: '#0f0f0f', padding: isMobile ? '16px 8px' : '20px 16px', textAlign: 'center', minWidth: 0 }}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.2rem' : '1.6rem', fontWeight: 300, color: '#c9a96e', lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</p>
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.5rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#3a3a3a', marginTop: '4px' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1e1e1e', marginBottom: '32px', overflowX: 'auto' }}>
          {['wears', 'collection'].map(tab => (
            <button key={tab} style={tabStyle(activeTab === tab)} onClick={() => setActiveTab(tab)}>{tab}</button>
          ))}
        </div>

        {/* Wears Tab */}
        {activeTab === 'wears' && (
          <div>
            {recent_wears.length === 0 ? (
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 300, color: '#2a2a2a', fontStyle: 'italic', textAlign: 'center', padding: '60px 0' }}>No wears logged yet</p>
            ) : (
              <div style={{ border: '1px solid #1a1a1a' }}>
                {recent_wears.map(entry => {
                  const isExpanded = expandedEntry === entry.id;
                  return (
                    <div
                      key={entry.id}
                      style={{ borderBottom: '1px solid #1a1a1a', background: isExpanded ? '#0f0f0f' : '#0a0a0a', cursor: 'pointer', transition: 'background 0.15s ease' }}
                      onClick={() => setExpandedEntry(isExpanded ? null : entry.id)}
                    >
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr auto' : '130px 1fr auto',
                        gap: isMobile ? '12px' : '24px',
                        padding: isMobile ? '16px' : '20px 28px',
                        alignItems: 'center',
                      }}>
                        {!isMobile && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', color: '#3a3a3a', letterSpacing: '0.05em' }}>{formatDate(entry.date_worn)}</p>}
                        <div>
                          {isMobile && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', color: '#3a3a3a', letterSpacing: '0.05em', marginBottom: '4px' }}>{formatDate(entry.date_worn)}</p>}
                          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '3px' }}>{entry.fragrance_brand}</p>
                          <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.1rem' : '1.2rem', fontWeight: 300, color: '#f5f0eb' }}>{entry.fragrance_name}</p>
                          {!isExpanded && (
                            <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                              {entry.occasion && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#3a3a3a', border: '1px solid #1a1a1a', padding: '2px 7px' }}>{entry.occasion}</span>}
                              {entry.mood && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#3a3a3a', border: '1px solid #1a1a1a', padding: '2px 7px' }}>{entry.mood}</span>}
                              {entry.compliments > 0 && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#c9a96e', border: '1px solid #1a1a1a', padding: '2px 7px' }}>{entry.compliments} compliments</span>}
                            </div>
                          )}
                        </div>
                        <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', color: isExpanded ? '#c9a96e' : '#2a2a2a' }}>{isExpanded ? '▲' : '▼'}</span>
                      </div>
                      {isExpanded && (
                        <div style={{ padding: isMobile ? '0 16px 20px' : '0 28px 24px', borderTop: '1px solid #141414' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: '20px', paddingTop: '20px' }}>
                            <div>
                              <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#2a2a2a', marginBottom: '12px' }}>Context</p>
                              {entry.occasion && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: '#6b6460', marginBottom: '6px' }}>{entry.occasion}</p>}
                              {entry.mood && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: '#6b6460', marginBottom: '6px' }}>{entry.mood}</p>}
                              {entry.compliments > 0 && <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', color: '#c9a96e' }}>{entry.compliments} compliments</p>}
                            </div>
                            <div>
                              <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#2a2a2a', marginBottom: '12px' }}>Conditions</p>
                              {entry.weather && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: '#6b6460', marginBottom: '6px' }}>{entry.weather}</p>}
                              {entry.temperature && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: '#6b6460' }}>{entry.temperature}°C</p>}
                            </div>
                            <div style={isMobile ? { gridColumn: '1 / -1' } : {}}>
                              <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#2a2a2a', marginBottom: '12px' }}>Performance</p>
                              {entry.longevity_rating && <div style={{ marginBottom: '10px' }}><RatingDots value={entry.longevity_rating} /><p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.62rem', color: '#3a3a3a', marginTop: '3px' }}>{longevityLabels[entry.longevity_rating]}</p></div>}
                              {entry.sillage_rating && <div><RatingDots value={entry.sillage_rating} /><p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.62rem', color: '#3a3a3a', marginTop: '3px' }}>{sillageLabels[entry.sillage_rating]}</p></div>}
                            </div>
                          </div>
                          {entry.notes && <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem', fontStyle: 'italic', color: '#4a4a4a', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #141414', lineHeight: 1.6 }}>"{entry.notes}"</p>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Collection Tab */}
        {activeTab === 'collection' && (
          <div>
            {library.length === 0 ? (
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 300, color: '#2a2a2a', fontStyle: 'italic', textAlign: 'center', padding: '60px 0' }}>No fragrances in collection yet</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1px', background: '#1a1a1a', border: '1px solid #1a1a1a' }}>
                {library.map(item => (
                  <div key={item.id} style={{ background: '#0f0f0f', padding: isMobile ? '18px 16px' : '24px 28px' }}>
                    <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '5px' }}>{item.brand}</p>
                    <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '10px' }}>{item.name}</p>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {item.main_accord && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.52rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#c9a96e', border: '1px solid rgba(201,169,110,0.3)', padding: '2px 7px', display: 'inline-block' }}>{item.main_accord}</span>}
                      {item.type && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.52rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#3a3a3a', border: '1px solid #1a1a1a', padding: '2px 7px', display: 'inline-block' }}>{item.type}</span>}
                    </div>
                    {item.price_gbp && <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '0.95rem', color: '#4a4a4a', marginTop: '8px' }}>£{item.price_gbp}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default PublicProfile;